import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import * as XLSX from "xlsx";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import Select from "../../components/ui/Select";
import Input from "../../components/ui/Input";
import { useAuth } from "../../contexts/AuthContext";
import {
  getWorkSubmittedAt,
  getWorkSubmitterId,
  isApprovalsSubmitterUser,
} from "../../lib/workSubmissionMeta";
import {
  customers as customersAPI,
  surveys as surveysAPI,
  civilWork as civilWorkAPI,
  transportWork as transportWorkAPI,
  technicalWork as technicalWorkAPI,
  commissioningWork as commissioningWorkAPI,
  technicalReportTokens,
  workCompletionReportTokens,
  users as usersAPI,
  notifications as notificationsAPI,
  clusters as clustersAPI,
  regions as regionsAPI,
} from "../../lib/database";
import { useToast } from "../../components/ui/Toast";
import { generateSurveyReport } from "../customer-database/utils/generateSurveyReport";
import SurveyReportModalBody from "../customer-database/components/SurveyReportModalBody";
import TechnicalView from "../customer-detail/components/TechnicalView";

const APPROVALS_LAST_QUERY_KEY = "approvals:lastQuery";

const DEFAULT_APPROVALS_STATE = {
  search: "",
  cluster: "all",
  region: [],
  submittedBy: "all",
  surveyCompletedOn: "",
  statusFilter: "all",
  activeTab: "survey",
};

/** Label + hint for the shared datetime-local “phase completed” filter (URL key stays surveyCompletedOn). */
const PHASE_COMPLETE_FILTER_COPY = {
  survey: {
    label: "Survey completed",
    hint: "Shows surveys completed on this day up to the selected time.",
  },
  transport: {
    label: "Transport complete",
    hint: "Shows transport work completed on this day up to the selected time.",
  },
  civil: {
    label: "Civil complete",
    hint: "Shows civil work completed on this day up to the selected time.",
  },
  technical: {
    label: "Technical complete",
    hint: "Shows technical installation completed on this day up to the selected time.",
  },
  commissioning: {
    label: "Commissioning complete",
    hint: "Shows commissioning completed on this day up to the selected time.",
  },
};

function completionRangeFromDatetimeLocal(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (s.includes("T")) {
    const dateStr = s.slice(0, 10);
    const timeStr = s.slice(11, 19) || "";
    const isMidnight =
      !timeStr || timeStr === "00:00" || timeStr.startsWith("00:00:");
    return {
      from: `${dateStr}T00:00:00`,
      to: isMidnight ? `${dateStr}T23:59:59.999` : s,
    };
  }
  return { from: `${s}T00:00:00`, to: `${s}T23:59:59.999` };
}

function rowTimestampMatchesCompletionFilter(phaseOn, ts) {
  const p = (phaseOn || "").trim();
  if (!p) return true;
  if (!ts) return true;
  const rowTime = new Date(ts).getTime();
  if (Number.isNaN(rowTime)) return false;
  if (p.includes("T")) {
    const dateStr = p.slice(0, 10);
    const timeStr = p.slice(11, 19) || "";
    const isMidnight =
      !timeStr || timeStr === "00:00" || timeStr.startsWith("00:00:");
    const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
    const until = isMidnight
      ? new Date(`${dateStr}T23:59:59.999`).getTime()
      : new Date(p).getTime();
    if (rowTime < dayStart || rowTime > until) return false;
  } else {
    const rowDate = new Date(ts).toISOString().slice(0, 10);
    if (rowDate !== p) return false;
  }
  return true;
}

const formatDateTimeForExport = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const exportTechnicalApprovalsToExcel = (
  rows = [],
  surveyByCustomerId = {},
  technicalReportTokenByCustomerId = {},
) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("No technical records to export");
  }

  const baseUrl =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const toText = (v) => (v == null ? "" : String(v));
  const firstNonEmpty = (...vals) => {
    for (const v of vals) {
      const s = toText(v).trim();
      if (s) return s;
    }
    return "";
  };

  const feetToMeters = (feetValue) => {
    const feet = Number.parseFloat(feetValue);
    if (!Number.isFinite(feet)) return "";
    return (feet * 0.3048).toFixed(2);
  };

  const formatCableLength = (feetValue, metersValue) => {
    const meters =
      String(metersValue ?? "").trim() || feetToMeters(feetValue) || "";
    if (!meters) return "";
    return `${meters} m`;
  };

  const exportData = rows.map((row) => {
    const customer = row.customers || {};
    const wiring = row.wiring || {};
    const wiringInverter = row.wiring_inverter || {};
    const testing = row.testing || {};
    const survey = surveyByCustomerId[row.customer_id] || null;
    const techToken = technicalReportTokenByCustomerId[row.customer_id];
    const technicalReportLink =
      baseUrl && techToken
        ? `${baseUrl}/technical-report/${encodeURIComponent(techToken)}`
        : "";

    return {
      SN: "",
      "CIN Number": toText(customer.cin),
      "USN Number": toText(customer.usn),
      "Consumer Name": toText(customer.name),
      "Aadhaar Number": toText(customer.aadhaar_number),
      "Structure Type": toText(survey?.roof_type),
      "Panel-1": "",
      "Panel-2": "",
      "Panel-3": "",
      "Panel-4": "",
      "Inverter Serial Num.": "",
      "AC/DCDB": firstNonEmpty(
        testing.acdcdb,
        testing.acDcDb,
        testing.ac_dcdb,
        testing.ac_dc_db,
      ),
      Black: formatCableLength(wiring.dcBlackFeet, null),
      Red: formatCableLength(wiring.dcRedFeet, null),
      "AC Cable": formatCableLength(wiring.acFeet, wiring.acMeters),
      "Earthing Cable": formatCableLength(
        wiring.earthingFeet,
        wiring.earthingMeters,
      ),
      "Civil Work": firstNonEmpty(
        customer.status === "technical_complete" ? "Completed" : "",
        row.approval_status === "approved" ? "Approved" : "",
      ),
      "Date of Completion": formatDateTimeForExport(
        row.installation_completed_at,
      ),
      "Date of Inspection": formatDateTimeForExport(row.approval_decision_at),
      Remarks: firstNonEmpty(
        row.notes,
        row.disapproval_reason,
        toText(wiringInverter.inverterMake || wiringInverter.inverter_make)
          ? `Inverter Make: ${toText(
              wiringInverter.inverterMake || wiringInverter.inverter_make,
            )}`
          : "",
      ),
      "Technical Report": technicalReportLink,
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData);

  // Make Technical Report column clickable hyperlinks
  const keys = Object.keys(exportData[0] || {});
  const technicalReportCol = keys.indexOf("Technical Report");
  if (technicalReportCol >= 0) {
    for (let i = 0; i < exportData.length; i++) {
      const url = exportData[i]["Technical Report"];
      if (url) {
        const cellRef = XLSX.utils.encode_cell({
          r: i + 1,
          c: technicalReportCol,
        });
        if (ws[cellRef]) {
          ws[cellRef].l = { Target: url, Tooltip: "Open technical report" };
        }
      }
    }
  }

  ws["!cols"] = Object.keys(exportData[0] || {}).map((key) => ({
    wch: Math.max(14, key.length + 2),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Technical Approvals");
  const timestamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `technical_approvals_${timestamp}.xlsx`);
};

const exportCommissioningApprovalsToExcel = (
  rows = [],
  workCompletionTokenByCustomerId = {},
) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("No commissioning records to export");
  }

  const baseUrl =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const toText = (v) => (v == null ? "" : String(v));
  const yesNo = (v) => (v ? "Yes" : "No");

  const exportData = rows.map((row) => {
    const customer = row.customers || {};
    const token = workCompletionTokenByCustomerId[row.customer_id];
    const workCompletionReportLink =
      baseUrl && token
        ? `${baseUrl}/work-completion-report/${encodeURIComponent(token)}`
        : "";

    return {
      SN: "",
      "CIN Number": toText(customer.cin),
      "USN Number": toText(customer.usn),
      "Consumer Name": toText(customer.name),
      Village: toText(customer.village),
      "Aadhaar Number": toText(customer.aadhaar_number),
      "Panel Number 1": toText(row.panel_number_1),
      "Panel Number 2": toText(row.panel_number_2),
      "Panel Number 3": toText(row.panel_number_3),
      "Panel Number 4": toText(row.panel_number_4),
      "Inverter Number": toText(row.inverter_number),
      "Cable Routing": yesNo(row.verification?.cable_routing),
      "Lightning Arrester Checked": yesNo(
        row.verification?.lightning_arrester_checked,
      ),
      "Civil Structure": yesNo(row.verification?.civil_structure),
      "Earthing Pits": yesNo(row.verification?.earthing_pits),
      "Inverter Functionality Confirmed": yesNo(
        row.verification?.inverter_functionality_confirmed,
      ),
      "Safety Systems Operation": yesNo(
        row.verification?.safety_systems_operation,
      ),
      "Date of Completion": formatDateTimeForExport(
        row.commissioning_completed_at,
      ),
      "Date of Inspection": formatDateTimeForExport(row.approval_decision_at),
      Remarks: toText(row.notes || row.disapproval_reason || ""),
      "Work Completion Report": workCompletionReportLink,
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData);

  // Make Work Completion Report column clickable hyperlinks
  const keys = Object.keys(exportData[0] || {});
  const reportCol = keys.indexOf("Work Completion Report");
  if (reportCol >= 0) {
    for (let i = 0; i < exportData.length; i++) {
      const url = exportData[i]["Work Completion Report"];
      if (url) {
        const cellRef = XLSX.utils.encode_cell({ r: i + 1, c: reportCol });
        if (ws[cellRef]) {
          ws[cellRef].l = {
            Target: url,
            Tooltip: "Open work completion report",
          };
        }
      }
    }
  }

  ws["!cols"] = Object.keys(exportData[0] || {}).map((key) => ({
    wch: Math.max(14, key.length + 2),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Commissioning Approvals");
  const timestamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `commissioning_approvals_${timestamp}.xlsx`);
};

const mergeUsersById = (...userLists) => {
  const byId = new Map();
  for (const list of userLists) {
    for (const u of list || []) {
      if (u?.id) byId.set(u.id, u);
    }
  }
  return [...byId.values()];
};

const collectSubmitterIds = (rows) => [
  ...new Set((rows || []).map((row) => row.submitted_by).filter(Boolean)),
];

const Approvals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userRole } = useAuth();
  const { showToast } = useToast();
  const hasInitializedFromUrlRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(DEFAULT_APPROVALS_STATE.activeTab); // survey | transport | civil | technical | commissioning
  const [surveys, setSurveys] = useState([]);
  const [transportItems, setTransportItems] = useState([]);
  const [civilItems, setCivilItems] = useState([]);
  const [technicalItems, setTechnicalItems] = useState([]);
  const [commissioningItems, setCommissioningItems] = useState([]);
  const [serverCount, setServerCount] = useState(0);

  const [scopedUsers, setScopedUsers] = useState([]);

  const [statusFilter, setStatusFilter] = useState(
    DEFAULT_APPROVALS_STATE.statusFilter,
  ); // all | pending | approved | disapproved

  const [filters, setFilters] = useState({
    search: DEFAULT_APPROVALS_STATE.search,
    cluster: DEFAULT_APPROVALS_STATE.cluster,
    region: DEFAULT_APPROVALS_STATE.region,
    submittedBy: DEFAULT_APPROVALS_STATE.submittedBy,
    surveyCompletedOn: DEFAULT_APPROVALS_STATE.surveyCompletedOn,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: DEFAULT_APPROVALS_STATE.search,
    cluster: DEFAULT_APPROVALS_STATE.cluster,
    region: DEFAULT_APPROVALS_STATE.region,
    submittedBy: DEFAULT_APPROVALS_STATE.submittedBy,
    surveyCompletedOn: DEFAULT_APPROVALS_STATE.surveyCompletedOn,
  });
  const [clusters, setClusters] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [regionSearch, setRegionSearch] = useState("");
  const regionDropdownRef = useRef(null);
  const [submittedByDropdownOpen, setSubmittedByDropdownOpen] = useState(false);
  const [submittedBySearch, setSubmittedBySearch] = useState("");
  const submittedByDropdownRef = useRef(null);
  // Shared disapprove modal (survey/transport/civil/technical/commissioning)
  const [disapproveModal, setDisapproveModal] = useState({
    open: false,
    type: "survey", // survey | transport | civil | technical | commissioning
    item: null,
    reason: "",
    saving: false,
  });

  const [viewReasonModal, setViewReasonModal] = useState({
    open: false,
    customerName: "",
    reason: "",
  });

  const [downloadingSurveyId, setDownloadingSurveyId] = useState(null);

  const [viewReportModal, setViewReportModal] = useState({
    open: false,
    row: null,
    customer: null,
    survey: null,
    loading: false,
  });
  const [viewTransportModal, setViewTransportModal] = useState({
    open: false,
    row: null,
  });
  const [viewCivilModal, setViewCivilModal] = useState({
    open: false,
    row: null,
  });
  const [viewTechnicalModal, setViewTechnicalModal] = useState({
    open: false,
    row: null,
    loading: false,
  });
  const [viewCommissioningModal, setViewCommissioningModal] = useState({
    open: false,
    row: null,
    loading: false,
  });
  const [isExportingTechnical, setIsExportingTechnical] = useState(false);
  const [isExportingCommissioning, setIsExportingCommissioning] =
    useState(false);

  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (userRole !== "super_admin") return null;
    const saved = localStorage.getItem("selectedProject");
    if (!saved || saved === "all") return null;
    return saved;
  });

  useEffect(() => {
    if (userRole !== "super_admin") {
      setSelectedProjectId(null);
      return;
    }
    const saved = localStorage.getItem("selectedProject");
    setSelectedProjectId(!saved || saved === "all" ? null : saved);
  }, [userRole]);

  const scopeFilters = useMemo(() => {
    if (!userRole) return {};

    if (userRole === "cluster_manager") {
      return { cluster_id: user?.cluster_id || null };
    }

    if (userRole === "project_admin") {
      return { project_id: user?.project_id || null };
    }

    if (userRole === "super_admin") {
      return { project_id: selectedProjectId || null };
    }

    return {};
  }, [userRole, user?.cluster_id, user?.project_id, selectedProjectId]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const readFiltersFromUrl = (params = searchParams) => {
    const next = { ...DEFAULT_APPROVALS_STATE };
    const tab = params.get("activeTab");
    if (
      tab === "survey" ||
      tab === "transport" ||
      tab === "civil" ||
      tab === "technical" ||
      tab === "commissioning"
    )
      next.activeTab = tab;
    const status = params.get("statusFilter");
    if (
      status === "all" ||
      status === "pending" ||
      status === "approved" ||
      status === "disapproved"
    )
      next.statusFilter = status;
    const search = params.get("search");
    if (search != null && search !== "") next.search = search;
    const cluster = params.get("cluster");
    if (cluster != null && cluster !== "") next.cluster = cluster;
    const region = params.get("region");
    if (region != null && region !== "")
      next.region = region
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const submittedBy =
      params.get("submittedBy") || params.get("teamMember");
    if (submittedBy != null && submittedBy !== "") next.submittedBy = submittedBy;
    const surveyCompletedOn = params.get("surveyCompletedOn");
    if (surveyCompletedOn != null && surveyCompletedOn !== "")
      next.surveyCompletedOn = surveyCompletedOn;
    return next;
  };

  const writeStateToUrl = (nextFilters, nextStatusFilter, nextActiveTab) => {
    const params = {};
    if (nextFilters?.search && nextFilters.search.trim() !== "")
      params.search = nextFilters.search.trim();
    if (nextFilters?.cluster && nextFilters.cluster !== "all")
      params.cluster = nextFilters.cluster;
    if (Array.isArray(nextFilters?.region) && nextFilters.region.length > 0)
      params.region = nextFilters.region.join(",");
    if (nextFilters?.submittedBy && nextFilters.submittedBy !== "all")
      params.submittedBy = nextFilters.submittedBy;
    if (nextFilters?.surveyCompletedOn && nextFilters.surveyCompletedOn !== "")
      params.surveyCompletedOn = nextFilters.surveyCompletedOn;
    if (nextStatusFilter && nextStatusFilter !== "all")
      params.statusFilter = nextStatusFilter;
    if (nextActiveTab && nextActiveTab !== "survey")
      params.activeTab = nextActiveTab;
    try {
      const nextQuery = new URLSearchParams(params).toString();
      localStorage.setItem(APPROVALS_LAST_QUERY_KEY, nextQuery);
    } catch {
      // ignore storage errors
    }
    setSearchParams(params, { replace: true });
  };

  // Initialize filters/tab/status from URL (or localStorage when navigating back with no query).
  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    let paramsToRead = searchParams;
    if (!searchParams?.toString()) {
      try {
        const savedQuery = localStorage.getItem(APPROVALS_LAST_QUERY_KEY);
        if (savedQuery) {
          paramsToRead = new URLSearchParams(savedQuery);
          setSearchParams(savedQuery, { replace: true });
        }
      } catch {
        // ignore
      }
    }
    const next = readFiltersFromUrl(paramsToRead);
    const initialFilters = {
      search: next.search,
      cluster: next.cluster,
      region: next.region,
      submittedBy: next.submittedBy,
      surveyCompletedOn: next.surveyCompletedOn || "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setStatusFilter(next.statusFilter);
    setActiveTab(next.activeTab);
    hasInitializedFromUrlRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL and localStorage in sync with applied filter/tab/status state.
  useEffect(() => {
    if (!hasInitializedFromUrlRef.current) return;
    writeStateToUrl(appliedFilters, statusFilter, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, statusFilter, activeTab]);

  useEffect(() => {
    if (!regionDropdownOpen && !submittedByDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        regionDropdownOpen &&
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(e.target)
      ) {
        setRegionDropdownOpen(false);
      }
      if (
        submittedByDropdownOpen &&
        submittedByDropdownRef.current &&
        !submittedByDropdownRef.current.contains(e.target)
      ) {
        setSubmittedByDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [regionDropdownOpen, submittedByDropdownOpen]);

  const statusFilterOptions = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "disapproved", label: "Disapproved" },
  ];

  const formatStatusLabel = (value) => {
    if (!value) return "—";
    return value
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getApprovalLabel = (approvalStatus) => {
    const normalized = (approvalStatus || "pending").toLowerCase();
    if (normalized === "approved") return "Approved";
    if (normalized === "disapproved") return "Disapproved";
    return "Pending";
  };

  const getApprovalColor = (approvalStatus) => {
    const normalized = (approvalStatus || "pending").toLowerCase();
    const colors = {
      pending:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      approved:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      disapproved:
        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };
    return (
      colors[normalized] ||
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
    );
  };

  const fetchScopedUsers = async (overrideProjectId = null) => {
    if (userRole === "cluster_manager" && user?.cluster_id) {
      return usersAPI.getByClusterId(user.cluster_id);
    }
    if (userRole === "project_admin" && user?.project_id) {
      return usersAPI.getByProjectId(user.project_id);
    }
    const projectId = overrideProjectId ?? selectedProjectId;
    if (userRole === "super_admin" && projectId) {
      return usersAPI.getByProjectId(projectId);
    }
    return usersAPI.getAll();
  };

  const fetchApprovalsData = async (overrideProjectId = null, options = {}) => {
    try {
      setLoading(true);

      const approvalStatus =
        statusFilter && statusFilter !== "all" ? statusFilter : null;
      const apiScope = { ...scopeFilters };
      if (userRole === "super_admin") {
        apiScope.project_id = overrideProjectId ?? selectedProjectId ?? null;
      }
      if (appliedFilters.cluster !== "all")
        apiScope.cluster_id = appliedFilters.cluster;

      // Fetch clusters and regions for filter dropdowns (scoped by role)
      let clustersData = [];
      let regionsData = [];
      if (userRole === "cluster_manager" && user?.cluster_id) {
        const cluster = await clustersAPI
          .getById(user.cluster_id)
          .catch(() => null);
        clustersData = cluster ? [cluster] : [];
        regionsData = cluster?.project_id
          ? await regionsAPI.getByProjectId(cluster.project_id).catch(() => [])
          : [];
      } else if (userRole === "project_admin" && user?.project_id) {
        const projId = user.project_id;
        [clustersData, regionsData] = await Promise.all([
          clustersAPI.getByProjectId(projId).catch(() => []),
          regionsAPI.getByProjectId(projId).catch(() => []),
        ]);
      } else if (userRole === "super_admin") {
        const projId = overrideProjectId ?? selectedProjectId;
        if (projId) {
          [clustersData, regionsData] = await Promise.all([
            clustersAPI.getByProjectId(projId).catch(() => []),
            regionsAPI.getByProjectId(projId).catch(() => []),
          ]);
        } else {
          [clustersData, regionsData] = await Promise.all([
            clustersAPI.getAll().catch(() => []),
            regionsAPI.getAll().catch(() => []),
          ]);
        }
      }
      setClusters(clustersData || []);
      setRegions(regionsData || []);

      const commonFilters = {
        ...apiScope,
        approval_status: approvalStatus,
        region_id:
          Array.isArray(appliedFilters.region) &&
          appliedFilters.region.length > 0
            ? appliedFilters.region
            : null,
        submitted_by:
          appliedFilters.submittedBy && appliedFilters.submittedBy !== "all"
            ? appliedFilters.submittedBy
            : null,
        search: (appliedFilters.search || "").trim() || null,
      };

      const completionRange = completionRangeFromDatetimeLocal(
        appliedFilters.surveyCompletedOn,
      );
      if (completionRange) {
        if (activeTab === "survey") {
          commonFilters.submitted_from = completionRange.from;
          commonFilters.submitted_to = completionRange.to;
        } else {
          commonFilters.phase_completed_from = completionRange.from;
          commonFilters.phase_completed_to = completionRange.to;
        }
      }

      const paging = {
        page: options.page ?? page,
        pageSize: options.pageSize ?? pageSize,
      };

      const projectIdForUsers =
        apiScope.project_id ?? overrideProjectId ?? selectedProjectId ?? null;

      const [result, scopedUsersList] = await Promise.all([
        activeTab === "survey"
          ? surveysAPI.getForApprovalsPaged(commonFilters, paging)
          : activeTab === "transport"
            ? transportWorkAPI.getForApprovalsPaged(commonFilters, paging)
            : activeTab === "civil"
              ? civilWorkAPI.getForApprovalsPaged(commonFilters, paging)
              : activeTab === "technical"
                ? technicalWorkAPI.getForApprovalsPaged(commonFilters, paging)
                : commissioningWorkAPI.getForApprovalsPaged(
                    commonFilters,
                    paging,
                  ),
        fetchScopedUsers(projectIdForUsers),
      ]);

      const pageRows = result.data || [];

      setServerCount(result.count || 0);
      setSurveys(activeTab === "survey" ? pageRows : []);
      setTransportItems(activeTab === "transport" ? pageRows : []);
      setCivilItems(activeTab === "civil" ? pageRows : []);
      setTechnicalItems(activeTab === "technical" ? pageRows : []);
      setCommissioningItems(activeTab === "commissioning" ? pageRows : []);

      const activeList = pageRows;
      const scopedUserIds = new Set((scopedUsersList || []).map((u) => u.id));
      const missingSubmitterIds = collectSubmitterIds(activeList).filter(
        (id) => !scopedUserIds.has(id),
      );
      const extraSubmitters =
        missingSubmitterIds.length > 0
          ? await usersAPI.getByIds(missingSubmitterIds).catch(() => [])
          : [];
      setScopedUsers(mergeUsersById(scopedUsersList, extraSubmitters));
    } catch (err) {
      console.error("Error fetching approvals data:", err);
      setSurveys([]);
      setTransportItems([]);
      setCivilItems([]);
      setTechnicalItems([]);
      setCommissioningItems([]);
      setScopedUsers([]);
      setServerCount(0);
    } finally {
      setLoading(false);
    }
  };

  const buildCommonFiltersForTab = async ({
    tab,
    overrideProjectId = null,
    includeCompletionDate = true,
  }) => {
    const approvalStatus =
      statusFilter && statusFilter !== "all" ? statusFilter : null;
    const apiScope = { ...scopeFilters };
    if (userRole === "super_admin") {
      apiScope.project_id = overrideProjectId ?? selectedProjectId ?? null;
    }
    if (appliedFilters.cluster !== "all") {
      apiScope.cluster_id = appliedFilters.cluster;
    }

    const commonFilters = {
      ...apiScope,
      approval_status: approvalStatus,
      region_id:
        Array.isArray(appliedFilters.region) && appliedFilters.region.length > 0
          ? appliedFilters.region
          : null,
      submitted_by:
        appliedFilters.submittedBy && appliedFilters.submittedBy !== "all"
          ? appliedFilters.submittedBy
          : null,
      search: (appliedFilters.search || "").trim() || null,
    };

    if (includeCompletionDate) {
      const completionRange = completionRangeFromDatetimeLocal(
        appliedFilters.surveyCompletedOn,
      );
      if (completionRange) {
        if (tab === "survey") {
          commonFilters.submitted_from = completionRange.from;
          commonFilters.submitted_to = completionRange.to;
        } else {
          commonFilters.phase_completed_from = completionRange.from;
          commonFilters.phase_completed_to = completionRange.to;
        }
      }
    }

    return commonFilters;
  };

  const handleExportTechnical = async () => {
    try {
      setIsExportingTechnical(true);
      const commonFilters = await buildCommonFiltersForTab({
        tab: "technical",
        includeCompletionDate: true,
      });

      // Pull all pages for current filters so export matches filtered result set.
      const pageSizeForExport = 500;
      const allRows = [];
      let currentPage = 1;
      let total = 0;
      do {
        const result = await technicalWorkAPI.getForApprovalsPaged(
          commonFilters,
          { page: currentPage, pageSize: pageSizeForExport },
        );
        const chunk = result?.data || [];
        total = result?.count || 0;
        allRows.push(...chunk);
        currentPage += 1;
      } while (allRows.length < total);

      const filteredRows = allRows.filter((row) => matchesFilters(row));
      const surveyRows = await Promise.all(
        filteredRows.map((row) =>
          surveysAPI.getByCustomerId(row.customer_id).catch(() => null),
        ),
      );
      const surveyByCustomerId = {};
      filteredRows.forEach((row, idx) => {
        surveyByCustomerId[row.customer_id] = surveyRows[idx];
      });

      const customerIds = Array.from(
        new Set(filteredRows.map((r) => r.customer_id).filter(Boolean)),
      );
      const technicalReportTokenByCustomerId =
        await technicalReportTokens.createForCustomers(customerIds);

      exportTechnicalApprovalsToExcel(
        filteredRows,
        surveyByCustomerId,
        technicalReportTokenByCustomerId,
      );
      showToast("Technical approvals exported.", "success");
    } catch (error) {
      console.error("Failed to export technical approvals:", error);
      showToast(
        error?.message || "Failed to export technical approvals.",
        "error",
      );
    } finally {
      setIsExportingTechnical(false);
    }
  };

  const handleExportCommissioning = async () => {
    try {
      setIsExportingCommissioning(true);
      const commonFilters = await buildCommonFiltersForTab({
        tab: "commissioning",
        includeCompletionDate: true,
      });

      // Pull all pages for current filters so export matches filtered result set.
      const pageSizeForExport = 500;
      const allRows = [];
      let currentPage = 1;
      let total = 0;
      do {
        const result = await commissioningWorkAPI.getForApprovalsPaged(
          commonFilters,
          { page: currentPage, pageSize: pageSizeForExport },
        );
        const chunk = result?.data || [];
        total = result?.count || 0;
        allRows.push(...chunk);
        currentPage += 1;
      } while (allRows.length < total);

      const filteredRows = allRows.filter((row) => matchesFilters(row));
      const customerIds = Array.from(
        new Set(filteredRows.map((r) => r.customer_id).filter(Boolean)),
      );
      const workCompletionTokenByCustomerId =
        await workCompletionReportTokens.createForCustomers(customerIds);

      exportCommissioningApprovalsToExcel(
        filteredRows,
        workCompletionTokenByCustomerId,
      );
      showToast("Commissioning approvals exported.", "success");
    } catch (error) {
      console.error("Failed to export commissioning approvals:", error);
      showToast(
        error?.message || "Failed to export commissioning approvals.",
        "error",
      );
    } finally {
      setIsExportingCommissioning(false);
    }
  };

  useEffect(() => {
    fetchApprovalsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    userRole,
    statusFilter,
    scopeFilters?.project_id,
    scopeFilters?.cluster_id,
    appliedFilters.cluster,
    activeTab,
    page,
    pageSize,
    appliedFilters.search,
    appliedFilters.submittedBy,
    appliedFilters.surveyCompletedOn,
    JSON.stringify(appliedFilters.region || []),
  ]);

  const matchesFilters = (row) => {
    const customer = row.customers;
    if (!customer) return false;
    const selectedRegionIds = Array.isArray(appliedFilters.region)
      ? appliedFilters.region
      : [];
    if (
      selectedRegionIds.length > 0 &&
      !selectedRegionIds.includes(customer.region_id)
    )
      return false;
    if (appliedFilters.submittedBy !== "all") {
      const submitterId =
        activeTab === "survey" ? row.updated_by : row.submitted_by;
      if (appliedFilters.submittedBy === "not_set") {
        if (submitterId) return false;
      } else if (submitterId !== appliedFilters.submittedBy) {
        return false;
      }
    }
    const q = (appliedFilters.search || "").trim().toLowerCase();
    if (q) {
      const name = (customer.name || "").toLowerCase();
      const cin = (customer.cin || "").toLowerCase();
      if (!name.includes(q) && !cin.includes(q)) return false;
    }
    const phaseOn = (appliedFilters.surveyCompletedOn || "").trim();
    if (phaseOn) {
      let ts = null;
      if (activeTab === "survey") ts = row.submitted_at;
      else if (activeTab === "transport" || activeTab === "civil")
        ts = row.updated_at;
      else if (activeTab === "technical") ts = row.installation_completed_at;
      else if (activeTab === "commissioning")
        ts = row.commissioning_completed_at;
      if (
        !rowTimestampMatchesCompletionFilter(
          appliedFilters.surveyCompletedOn,
          ts,
        )
      )
        return false;
    }
    return true;
  };

  // With server-side paging, most filters are applied in the query.
  // We keep a small client-side pass for assignment-based and date edge cases.
  const filteredSurveys = useMemo(
    () => surveys.filter(matchesFilters),
    [
      surveys,
      activeTab,
      appliedFilters.region,
      appliedFilters.submittedBy,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
    ],
  );
  const filteredTransportItems = useMemo(
    () => transportItems.filter(matchesFilters),
    [
      transportItems,
      activeTab,
      appliedFilters.region,
      appliedFilters.submittedBy,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
    ],
  );
  const filteredCivilItems = useMemo(
    () => civilItems.filter(matchesFilters),
    [
      civilItems,
      activeTab,
      appliedFilters.region,
      appliedFilters.submittedBy,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
    ],
  );
  const filteredTechnicalItems = useMemo(
    () => technicalItems.filter(matchesFilters),
    [
      technicalItems,
      activeTab,
      appliedFilters.region,
      appliedFilters.submittedBy,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
    ],
  );
  const filteredCommissioningItems = useMemo(
    () => commissioningItems.filter(matchesFilters),
    [
      commissioningItems,
      activeTab,
      appliedFilters.region,
      appliedFilters.submittedBy,
      appliedFilters.search,
      appliedFilters.surveyCompletedOn,
    ],
  );

  useEffect(() => {
    if (userRole !== "super_admin") return;
    const handler = () => {
      const saved = localStorage.getItem("selectedProject");
      const nextProjectId = !saved || saved === "all" ? null : saved;
      setSelectedProjectId(nextProjectId);
      fetchApprovalsData(nextProjectId);
    };
    window.addEventListener("projectChanged", handler);
    return () => window.removeEventListener("projectChanged", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, selectedProjectId]);

  const userById = useMemo(
    () => new Map((scopedUsers || []).map((u) => [u.id, u])),
    [scopedUsers],
  );

  const submittedByFilterOptions = useMemo(() => {
    const submitters = (scopedUsers || [])
      .filter(isApprovalsSubmitterUser)
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
    return [
      { value: "all", label: "All Submitters" },
      { value: "not_set", label: "No submitter" },
      ...submitters.map((u) => ({
        value: u.id,
        label: `${u.unique_id ? `${u.unique_id} - ` : ""}${u.name || "Unnamed"}`,
      })),
    ];
  }, [scopedUsers]);

  const getSubmissionMeta = (row, phase) => {
    if (!row) return { submittedAt: null, submitterLabel: "—" };

    const submittedAt = getWorkSubmittedAt(row, phase);
    const submitterId = getWorkSubmitterId(row);
    const submitterLabel =
      userById.get(submitterId)?.name?.trim() || "—";

    return { submittedAt, submitterLabel };
  };

  const openDisapproveModal = (type, item) => {
    setDisapproveModal({
      open: true,
      type,
      item,
      reason: "",
      saving: false,
    });
  };

  const closeDisapproveModal = () => {
    if (disapproveModal.saving) return;
    setDisapproveModal({
      open: false,
      type: disapproveModal.type,
      item: null,
      reason: "",
      saving: false,
    });
  };

  const openViewReasonModalFor = async (type, item) => {
    setViewReasonModal({
      open: true,
      customerName: item?.customers?.name || "Customer",
      reason: item?.disapproval_reason || "Loading...",
    });

    try {
      if (!item?.id) return;
      let fresh = null;
      if (type === "survey") fresh = await surveysAPI.getById(item.id);
      if (type === "transport") fresh = await transportWorkAPI.getById(item.id);
      if (type === "civil") fresh = await civilWorkAPI.getById(item.id);
      if (type === "technical") fresh = await technicalWorkAPI.getById(item.id);
      if (type === "commissioning")
        fresh = await commissioningWorkAPI.getById(item.id);

      setViewReasonModal((prev) => ({
        ...prev,
        customerName: fresh?.customers?.name || prev.customerName,
        reason: fresh?.disapproval_reason || "—",
      }));
    } catch (e) {
      console.warn("Failed to load disapproval reason:", e);
      setViewReasonModal((prev) => ({
        ...prev,
        reason: prev.reason && prev.reason !== "Loading..." ? prev.reason : "—",
      }));
    }
  };

  const closeViewReasonModal = () => {
    setViewReasonModal({ open: false, customerName: "", reason: "" });
  };

  const handleDisapprove = async () => {
    const { type, item, reason } = disapproveModal;
    if (!item?.id) return;

    const payload = {
      approval_status: "disapproved",
      approval_decision_by: user?.id || null,
      approval_decision_at: new Date().toISOString(),
      disapproval_reason: reason?.trim() ? reason.trim() : null,
    };

    try {
      setDisapproveModal((prev) => ({ ...prev, saving: true }));

      if (type === "survey") {
        await surveysAPI.update(item.id, payload);
      } else if (type === "transport") {
        await transportWorkAPI.update(item.id, payload);
      } else if (type === "civil") {
        await civilWorkAPI.update(item.id, payload);
        // Important: do NOT change customer.status on disapproval.
        // If we change it to civil_inprogress, this row falls out of the approvals list
        // (since the query is based on completed statuses).
      } else if (type === "technical") {
        await technicalWorkAPI.update(item.id, payload);
        // Same as civil: keep customer.status unchanged so the row remains visible.
      } else if (type === "commissioning") {
        await commissioningWorkAPI.update(item.id, payload);
      }

      showToast("Disapproved.", "success");
      closeDisapproveModal();
      await fetchApprovalsData();
    } catch (err) {
      console.error("Disapprove failed:", err);
      showToast("Failed to disapprove.", "error");
      setDisapproveModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleApproveSurvey = async (survey) => {
    if (!survey?.id) return false;
    try {
      await surveysAPI.update(
        survey.id,
        {
          approval_status: "approved",
          approval_decision_by: user?.id || null,
          approval_decision_at: new Date().toISOString(),
          disapproval_reason: null,
        },
        { userId: user?.id },
      );
      await customersAPI.update(survey.customer_id, {
        status: "transport_pending",
      });
      showToast("Survey approved.", "success");
      await fetchApprovalsData();
      return true;
    } catch (err) {
      console.error("Approve survey failed:", err);
      showToast("Failed to approve survey.", "error");
      return false;
    }
  };

  const handleApproveTransport = async (transportItem) => {
    if (!transportItem?.id) return false;
    try {
      await transportWorkAPI.update(transportItem.id, {
        approval_status: "approved",
        approval_decision_by: user?.id || null,
        approval_decision_at: new Date().toISOString(),
        disapproval_reason: null,
      });
      await customersAPI.update(transportItem.customer_id, {
        status: "civil_pending",
      });
      showToast("Transport approved.", "success");
      await fetchApprovalsData();
      return true;
    } catch (err) {
      console.error("Approve transport failed:", err);
      showToast("Failed to approve transport.", "error");
      return false;
    }
  };

  const handleOpenViewReport = async (row) => {
    setViewReportModal({
      open: true,
      row,
      customer: null,
      survey: null,
      loading: true,
    });
    const surveyId = row.id;
    const customerId = row.customer_id;
    try {
      const [customer, survey] = await Promise.all([
        customersAPI.getById(customerId),
        surveysAPI.getById(surveyId),
      ]);
      setViewReportModal((prev) => ({
        ...prev,
        customer: customer || null,
        survey: survey || null,
        loading: false,
      }));
      if (!survey) {
        showToast("No survey data found for this customer", "error");
      }
    } catch (err) {
      console.error("Load survey report failed:", err);
      showToast(err?.message || "Failed to load survey report.", "error");
      setViewReportModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseViewReportModal = () => {
    setViewReportModal({
      open: false,
      row: null,
      customer: null,
      survey: null,
      loading: false,
    });
  };

  const handleDownloadSurveyReportFromModal = async () => {
    const { customer, survey } = viewReportModal;
    if (!customer || !survey) return;
    try {
      setDownloadingSurveyId(survey.id);
      showToast("Generating survey report...", "info");
      await generateSurveyReport(customer, survey, null, null);
      showToast("Survey report generated successfully", "success");
    } catch (err) {
      console.error("Download survey report failed:", err);
      showToast(err?.message || "Failed to generate survey report.", "error");
    } finally {
      setDownloadingSurveyId(null);
    }
  };

  const handleApproveFromViewReportModal = async () => {
    const { row } = viewReportModal;
    if (!row) return;
    const ok = await handleApproveSurvey(row);
    if (ok) handleCloseViewReportModal();
  };

  const handleDisapproveFromViewReportModal = () => {
    const { row } = viewReportModal;
    if (row) openDisapproveModal("survey", row);
    handleCloseViewReportModal();
  };

  const handleOpenViewTransportModal = (row) => {
    setViewTransportModal({ open: true, row });
  };

  const handleCloseViewTransportModal = () => {
    setViewTransportModal({ open: false, row: null });
  };

  const handleOpenViewCivilModal = (row) => {
    setViewCivilModal({ open: true, row });
  };

  const handleCloseViewCivilModal = () => {
    setViewCivilModal({ open: false, row: null });
  };

  const handleOpenViewTechnicalModal = async (row) => {
    if (!row?.id) return;
    setViewTechnicalModal({ open: true, row, loading: true });
    try {
      const fresh = await technicalWorkAPI.getById(row.id);
      setViewTechnicalModal({
        open: true,
        row: fresh || row,
        loading: false,
      });
    } catch (e) {
      console.warn("Failed to load technical work details:", e);
      setViewTechnicalModal({ open: true, row, loading: false });
    }
  };

  const handleCloseViewTechnicalModal = () => {
    setViewTechnicalModal({ open: false, row: null, loading: false });
  };

  const handleApproveCivil = async (civilItem) => {
    if (!civilItem?.id) return false;
    try {
      await civilWorkAPI.update(civilItem.id, {
        approval_status: "approved",
        approval_decision_by: user?.id || null,
        approval_decision_at: new Date().toISOString(),
        disapproval_reason: null,
      });
      await customersAPI.update(civilItem.customer_id, {
        status: "technical_pending",
      });
      showToast("Civil approved.", "success");
      await fetchApprovalsData();
      return true;
    } catch (err) {
      console.error("Approve civil failed:", err);
      showToast("Failed to approve civil.", "error");
      return false;
    }
  };

  const handleApproveTechnical = async (technicalItem) => {
    if (!technicalItem?.id) return false;
    try {
      await technicalWorkAPI.update(technicalItem.id, {
        approval_status: "approved",
        approval_decision_by: user?.id || null,
        approval_decision_at: new Date().toISOString(),
        disapproval_reason: null,
      });
      await customersAPI.update(technicalItem.customer_id, {
        status: "commissioning_not_started",
      });
      showToast("Technical approved.", "success");
      await fetchApprovalsData();
      return true;
    } catch (err) {
      console.error("Approve technical failed:", err);
      showToast("Failed to approve technical work.", "error");
      return false;
    }
  };

  const handleApproveTechnicalFromModal = async () => {
    const row = viewTechnicalModal.row;
    if (!row) return;
    const ok = await handleApproveTechnical(row);
    if (ok) handleCloseViewTechnicalModal();
  };

  const handleOpenViewCommissioningModal = async (row) => {
    if (!row?.id) return;
    setViewCommissioningModal({ open: true, row, loading: true });
    try {
      const fresh = await commissioningWorkAPI.getById(row.id);
      setViewCommissioningModal({
        open: true,
        row: fresh || row,
        loading: false,
      });
    } catch (e) {
      console.warn("Failed to load commissioning work details:", e);
      setViewCommissioningModal({ open: true, row, loading: false });
    }
  };

  const handleCloseViewCommissioningModal = () => {
    setViewCommissioningModal({ open: false, row: null, loading: false });
  };

  const handleApproveCommissioning = async (item) => {
    if (!item?.id) return false;
    try {
      await commissioningWorkAPI.update(item.id, {
        approval_status: "approved",
        approval_decision_by: user?.id || null,
        approval_decision_at: new Date().toISOString(),
        disapproval_reason: null,
      });
      showToast("Commissioning approved.", "success");
      await fetchApprovalsData();
      return true;
    } catch (e) {
      console.error(e);
      showToast("Failed to approve commissioning.", "error");
      return false;
    }
  };

  const handleApproveCommissioningFromModal = async () => {
    const row = viewCommissioningModal.row;
    if (!row) return;
    const ok = await handleApproveCommissioning(row);
    if (ok) handleCloseViewCommissioningModal();
  };

  const displayList =
    activeTab === "survey"
      ? filteredSurveys
      : activeTab === "transport"
        ? filteredTransportItems
        : activeTab === "civil"
          ? filteredCivilItems
          : activeTab === "technical"
            ? filteredTechnicalItems
            : filteredCommissioningItems;
  const itemsCount = serverCount || 0;

  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    statusFilter,
    appliedFilters.search,
    appliedFilters.cluster,
    appliedFilters.submittedBy,
    appliedFilters.surveyCompletedOn,
    // region is an array; stringify for stable dependency
    JSON.stringify(appliedFilters.region || []),
  ]);

  const totalPages = Math.max(1, Math.ceil(itemsCount / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedList = displayList;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading approvals...</p>
        </div>
      </div>
    );
  }

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFiltersAndSearch = () => {
    const defaults = {
      search: "",
      cluster: "all",
      region: [],
      submittedBy: "all",
      surveyCompletedOn: "",
    };
    setFilters(defaults);
    setAppliedFilters(defaults);
  };

  const hasActiveFilters =
    (filters.search || "").trim() !== "" ||
    filters.cluster !== "all" ||
    (Array.isArray(filters.region) && filters.region.length > 0) ||
    filters.submittedBy !== "all" ||
    (filters.surveyCompletedOn || "").trim() !== "";

  const regionOptions =
    regions?.map((r) => ({ value: r.id, label: r.name })) || [];
  const selectedRegionIds = Array.isArray(filters.region) ? filters.region : [];
  const filteredRegionOptions = regionSearch.trim()
    ? regionOptions.filter((opt) =>
        opt.label.toLowerCase().includes(regionSearch.toLowerCase()),
      )
    : regionOptions;
  const toggleRegion = (regionId) => {
    const next = selectedRegionIds.includes(regionId)
      ? selectedRegionIds.filter((id) => id !== regionId)
      : [...selectedRegionIds, regionId];
    setFilters((prev) => ({ ...prev, region: next }));
  };
  const regionDisplayLabel =
    selectedRegionIds.length === 0
      ? "All Regions"
      : selectedRegionIds.length === 1
        ? (regionOptions.find((o) => o.value === selectedRegionIds[0])?.label ??
          "1 selected")
        : `${selectedRegionIds.length} regions selected`;

  const filteredSubmittedByOptions = submittedBySearch.trim()
    ? submittedByFilterOptions.filter((opt) =>
        opt.label.toLowerCase().includes(submittedBySearch.toLowerCase()),
      )
    : submittedByFilterOptions;
  const submittedByDisplayLabel =
    submittedByFilterOptions.find((o) => o.value === filters.submittedBy)
      ?.label ?? "All Submitters";
  const selectSubmittedBy = (value) => {
    setFilters((prev) => ({ ...prev, submittedBy: value }));
    setSubmittedByDropdownOpen(false);
    setSubmittedBySearch("");
  };

  return (
    <>
      <Helmet>
        <title>Approvals</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                  Approvals
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Review Survey/Transport/Civil/Technical/Commissioning
                  submissions and approve/disapprove.
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={activeTab === "survey" ? "default" : "outline"}
                    size="sm"
                    iconName="ClipboardCheck"
                    iconPosition="left"
                    onClick={() => setActiveTab("survey")}
                  >
                    Survey
                  </Button>
                  <Button
                    variant={activeTab === "transport" ? "default" : "outline"}
                    size="sm"
                    iconName="Truck"
                    iconPosition="left"
                    onClick={() => setActiveTab("transport")}
                  >
                    Transport
                  </Button>
                  <Button
                    variant={activeTab === "civil" ? "default" : "outline"}
                    size="sm"
                    iconName="HardHat"
                    iconPosition="left"
                    onClick={() => setActiveTab("civil")}
                  >
                    Civil
                  </Button>
                  <Button
                    variant={activeTab === "technical" ? "default" : "outline"}
                    size="sm"
                    iconName="Wrench"
                    iconPosition="left"
                    onClick={() => setActiveTab("technical")}
                  >
                    Technical
                  </Button>
                  <Button
                    variant={
                      activeTab === "commissioning" ? "default" : "outline"
                    }
                    size="sm"
                    iconName="ClipboardCheck"
                    iconPosition="left"
                    onClick={() => setActiveTab("commissioning")}
                  >
                    Commissioning
                  </Button>
                </div>

                <div className="max-w-xs w-full">
                  <Select
                    label="Filter by approval status"
                    options={statusFilterOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                  />
                </div>
              </div>
            </div>

            {/* Search and Filters - similar to customer database */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-4 md:mb-6 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="w-full sm:max-w-md flex gap-2 items-center">
                  <Input
                    type="search"
                    placeholder="Search by customer name or CIN..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className="w-full"
                  />
                  <Button
                    variant="default"
                    size="default"
                    onClick={handleSearch}
                    className="shrink-0"
                  >
                    Search
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Filter" size={16} />
                  <span className="font-medium">
                    {itemsCount}{" "}
                    {activeTab === "survey"
                      ? "survey"
                      : activeTab === "transport"
                        ? "transport"
                        : activeTab === "civil"
                          ? "civil"
                          : "technical"}{" "}
                    item{itemsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Select
                  label="Cluster"
                  options={[
                    { value: "all", label: "All Clusters" },
                    ...(clusters?.map((c) => ({
                      value: c.id,
                      label: c.name,
                    })) || []),
                  ]}
                  value={filters.cluster}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, cluster: value }))
                  }
                />
                <div
                  className="flex flex-col gap-1 relative"
                  ref={regionDropdownRef}
                >
                  <label className="text-sm font-medium text-foreground">
                    Region
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setRegionDropdownOpen((o) => !o);
                      if (!regionDropdownOpen) setRegionSearch("");
                    }}
                    className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${selectedRegionIds.length === 0 ? "text-muted-foreground" : "text-black dark:text-white"}`}
                  >
                    <span className="truncate text-left">
                      {regionDisplayLabel}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 transition-transform ${regionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {regionDropdownOpen && (
                    <div className="absolute left-0 bottom-full z-[10000] mb-1 w-full min-w-[200px] max-h-[280px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-border">
                        <input
                          type="search"
                          placeholder="Search regions..."
                          value={regionSearch}
                          onChange={(e) => setRegionSearch(e.target.value)}
                          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          autoFocus
                        />
                      </div>
                      <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                        {filteredRegionOptions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            No regions match
                          </div>
                        ) : (
                          filteredRegionOptions.map((opt) => {
                            const isSelected = selectedRegionIds.includes(
                              opt.value,
                            );
                            return (
                              <div
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                onClick={() => toggleRegion(opt.value)}
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                    isSelected
                                      ? "bg-primary border-primary text-primary-foreground"
                                      : "border-input"
                                  }`}
                                >
                                  {isSelected ? (
                                    <Check className="h-3 w-3" />
                                  ) : null}
                                </span>
                                <span className="truncate">{opt.label}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="flex flex-col gap-1 relative"
                  ref={submittedByDropdownRef}
                >
                  <label className="text-sm font-medium text-foreground">
                    Submitted By
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmittedByDropdownOpen((o) => !o);
                      if (!submittedByDropdownOpen) setSubmittedBySearch("");
                    }}
                    className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${filters.submittedBy === "all" ? "text-muted-foreground" : "text-black dark:text-white"}`}
                  >
                    <span className="truncate text-left">
                      {submittedByDisplayLabel}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 transition-transform ${submittedByDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {submittedByDropdownOpen && (
                    <div className="absolute left-0 bottom-full z-[10000] mb-1 w-full min-w-[220px] max-h-[280px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-border">
                        <input
                          type="search"
                          placeholder="Search submitters..."
                          value={submittedBySearch}
                          onChange={(e) =>
                            setSubmittedBySearch(e.target.value)
                          }
                          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          autoFocus
                        />
                      </div>
                      <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                        {filteredSubmittedByOptions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            No submitters match
                          </div>
                        ) : (
                          filteredSubmittedByOptions.map((opt) => {
                            const isSelected =
                              filters.submittedBy === opt.value;
                            return (
                              <div
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${isSelected ? "bg-accent/50" : ""}`}
                                onClick={() => selectSubmittedBy(opt.value)}
                              >
                                <span className="truncate">{opt.label}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">
                    {
                      (
                        PHASE_COMPLETE_FILTER_COPY[activeTab] ??
                        PHASE_COMPLETE_FILTER_COPY.survey
                      ).label
                    }
                  </label>
                  <input
                    key={`phase-complete-${activeTab}`}
                    type="datetime-local"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={filters.surveyCompletedOn || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        surveyCompletedOn: e.target.value || "",
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {
                      (
                        PHASE_COMPLETE_FILTER_COPY[activeTab] ??
                        PHASE_COMPLETE_FILTER_COPY.survey
                      ).hint
                    }
                  </p>
                </div>
              </div>
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    iconPosition="left"
                    onClick={handleClearFiltersAndSearch}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  {activeTab === "survey"
                    ? `Surveys (${itemsCount})`
                    : activeTab === "transport"
                      ? `Transport (${itemsCount})`
                      : activeTab === "civil"
                        ? `Civil (${itemsCount})`
                        : activeTab === "technical"
                          ? `Technical (${itemsCount})`
                          : `Commissioning (${itemsCount})`}
                </h2>
                {activeTab === "technical" && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    disabled={isExportingTechnical}
                    onClick={handleExportTechnical}
                  >
                    {isExportingTechnical ? "Exporting..." : "Export"}
                  </Button>
                )}
                {activeTab === "commissioning" && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    disabled={isExportingCommissioning}
                    onClick={handleExportCommissioning}
                  >
                    {isExportingCommissioning ? "Exporting..." : "Export"}
                  </Button>
                )}
              </div>

              {itemsCount > 0 ? (
                <div className="overflow-x-auto">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div className="text-sm text-muted-foreground">
                      Showing{" "}
                      <span className="font-medium text-foreground">
                        {itemsCount === 0 ? 0 : startIdx + 1}
                      </span>{" "}
                      -{" "}
                      <span className="font-medium text-foreground">
                        {Math.min(itemsCount, startIdx + paginatedList.length)}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium text-foreground">
                        {itemsCount}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground">
                        Rows per page
                      </span>
                      <select
                        className="h-9 rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm text-foreground"
                        value={pageSize}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 25;
                          setPageSize(next);
                          setPage(1);
                        }}
                      >
                        {[10, 25, 50, 100].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          CIN
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Cluster
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Village
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Submitted By
                        </th>
                        {activeTab === "survey" && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                            Completed on
                          </th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Approval
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-border">
                      {paginatedList.map((row) => {
                        const customer = row.customers;
                        const approvalStatus = row.approval_status || "pending";
                        const type = activeTab;

                        return (
                          <tr key={row.id}>
                            <td className="px-4 py-3 text-sm text-foreground">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/customer-detail/${row.customer_id}`,
                                  )
                                }
                                className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                              >
                                {customer?.name || "—"}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground font-mono">
                              {customer?.cin || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {customer?.clusters?.name || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {customer?.village || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {getSubmissionMeta(row, activeTab).submitterLabel}
                            </td>
                            {activeTab === "survey" && (
                              <td className="px-4 py-3 text-sm text-foreground">
                                {row.submitted_at
                                  ? new Date(
                                      row.submitted_at,
                                    ).toLocaleDateString("en-IN", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "—"}
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs ${getApprovalColor(
                                  approvalStatus,
                                )}`}
                              >
                                {getApprovalLabel(approvalStatus)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {formatStatusLabel(customer?.status)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {type === "survey" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="FileText"
                                    iconPosition="left"
                                    onClick={() => handleOpenViewReport(row)}
                                    loading={
                                      viewReportModal.open &&
                                      viewReportModal.row?.id === row.id &&
                                      viewReportModal.loading
                                    }
                                    disabled={
                                      viewReportModal.open &&
                                      viewReportModal.loading
                                    }
                                  >
                                    View Report
                                  </Button>
                                )}

                                {type !== "survey" &&
                                  type !== "transport" &&
                                  type !== "civil" &&
                                  type !== "technical" &&
                                  type !== "commissioning" &&
                                  (approvalStatus === "disapproved" ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="MessageSquareText"
                                      iconPosition="left"
                                      onClick={() =>
                                        openViewReasonModalFor(type, row)
                                      }
                                    >
                                      View reason
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      iconName="XCircle"
                                      iconPosition="left"
                                      onClick={() =>
                                        openDisapproveModal(type, row)
                                      }
                                    >
                                      Disapprove
                                    </Button>
                                  ))}

                                {type === "survey" ? null : type ===
                                  "transport" ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Eye"
                                    iconPosition="left"
                                    onClick={() =>
                                      handleOpenViewTransportModal(row)
                                    }
                                  >
                                    View Details
                                  </Button>
                                ) : type === "civil" ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Eye"
                                    iconPosition="left"
                                    onClick={() =>
                                      handleOpenViewCivilModal(row)
                                    }
                                  >
                                    View Details
                                  </Button>
                                ) : type === "technical" ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Eye"
                                    iconPosition="left"
                                    onClick={() =>
                                      handleOpenViewTechnicalModal(row)
                                    }
                                    loading={
                                      viewTechnicalModal.loading &&
                                      viewTechnicalModal.row?.id === row.id
                                    }
                                    disabled={
                                      viewTechnicalModal.loading &&
                                      viewTechnicalModal.row?.id === row.id
                                    }
                                  >
                                    View Details
                                  </Button>
                                ) : type === "commissioning" ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Eye"
                                    iconPosition="left"
                                    onClick={() =>
                                      handleOpenViewCommissioningModal(row)
                                    }
                                    loading={
                                      viewCommissioningModal.loading &&
                                      viewCommissioningModal.row?.id === row.id
                                    }
                                    disabled={
                                      viewCommissioningModal.loading &&
                                      viewCommissioningModal.row?.id === row.id
                                    }
                                  >
                                    View Details
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                      <div className="text-sm text-muted-foreground">
                        Page{" "}
                        <span className="font-medium text-foreground">
                          {safePage}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium text-foreground">
                          {totalPages}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Prev
                        </Button>

                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, idx) => {
                            const start = Math.max(
                              1,
                              Math.min(
                                safePage - 2,
                                totalPages - Math.min(5, totalPages) + 1,
                              ),
                            );
                            const pageNum = start + idx;
                            return pageNum <= totalPages ? pageNum : null;
                          },
                        )
                          .filter(Boolean)
                          .map((pageNum) => (
                            <Button
                              key={pageNum}
                              variant={
                                pageNum === safePage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          ))}

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage >= totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon
                    name="ClipboardCheck"
                    size={48}
                    className="mx-auto mb-4 text-muted-foreground"
                  />
                  <p className="text-muted-foreground">No items to review.</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* View Survey Report Modal */}
      {viewReportModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Survey Report
                {viewReportModal.customer?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewReportModal.customer.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewReportModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
              {viewReportModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                </div>
              ) : viewReportModal.survey && viewReportModal.customer ? (
                <SurveyReportModalBody
                  survey={viewReportModal.survey}
                  customer={viewReportModal.customer}
                />
              ) : !viewReportModal.loading ? (
                <div className="text-center py-12 text-muted-foreground">
                  No survey data available.
                </div>
              ) : null}
            </div>

            {/* Footer: Approve, Disapprove, Download report */}
            {viewReportModal.survey &&
              viewReportModal.customer &&
              !viewReportModal.loading && (
                <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
                  <div className="text-xs text-muted-foreground">
                    <p>
                      Submitted at:{" "}
                      <span className="font-medium text-foreground">
                        {getSubmissionMeta(viewReportModal.row, "survey")
                          .submittedAt
                          ? new Date(
                              getSubmissionMeta(viewReportModal.row, "survey")
                                .submittedAt,
                            ).toLocaleString("en-IN")
                          : "—"}
                      </span>
                    </p>
                    <p>
                      Submitted by:{" "}
                      <span className="font-medium text-foreground">
                        {
                          getSubmissionMeta(viewReportModal.row, "survey")
                            .submitterLabel
                        }
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 ml-auto">
                    {viewReportModal.row?.approval_status === "disapproved" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquareText"
                        iconPosition="left"
                        onClick={() => {
                          openViewReasonModalFor("survey", viewReportModal.row);
                          handleCloseViewReportModal();
                        }}
                      >
                        View reason
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="XCircle"
                        iconPosition="left"
                        onClick={handleDisapproveFromViewReportModal}
                      >
                        Disapprove
                      </Button>
                    )}
                    {viewReportModal.row?.approval_status !== "approved" && (
                      <Button
                        variant="success"
                        size="sm"
                        iconName="UserCheck"
                        iconPosition="left"
                        onClick={handleApproveFromViewReportModal}
                      >
                        Approve
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={handleDownloadSurveyReportFromModal}
                      loading={
                        downloadingSurveyId === viewReportModal.survey?.id
                      }
                    >
                      Download report
                    </Button>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* View Transport Details Modal */}
      {viewTransportModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Transport Details
                {viewTransportModal.row?.customers?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewTransportModal.row.customers.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewTransportModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold text-foreground mb-3">
                    Customer &amp; Kit Photo
                  </h3>
                  {viewTransportModal.row?.customer_kit_photo ? (
                    <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                      <img
                        src={viewTransportModal.row.customer_kit_photo}
                        alt="Customer and kit"
                        className="w-full h-full object-contain bg-muted"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border text-muted-foreground text-sm">
                      No image uploaded
                    </div>
                  )}
                </div>

                <div className="p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold text-foreground mb-3">
                    Panel Structure Photo
                  </h3>
                  {viewTransportModal.row?.panel_structure_photo ? (
                    <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                      <img
                        src={viewTransportModal.row.panel_structure_photo}
                        alt="Panel structure"
                        className="w-full h-full object-contain bg-muted"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border text-muted-foreground text-sm">
                      No image uploaded
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
              <div className="text-xs text-muted-foreground">
                <p>
                  Submitted at:{" "}
                  <span className="font-medium text-foreground">
                    {getSubmissionMeta(viewTransportModal.row, "transport")
                      .submittedAt
                      ? new Date(
                          getSubmissionMeta(viewTransportModal.row, "transport")
                            .submittedAt,
                        ).toLocaleString("en-IN")
                      : "—"}
                  </span>
                </p>
                <p>
                  Submitted by:{" "}
                  <span className="font-medium text-foreground">
                    {
                      getSubmissionMeta(viewTransportModal.row, "transport")
                        .submitterLabel
                    }
                  </span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 ml-auto">
                {viewTransportModal.row?.approval_status === "disapproved" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="MessageSquareText"
                    iconPosition="left"
                    onClick={() => {
                      openViewReasonModalFor(
                        "transport",
                        viewTransportModal.row,
                      );
                      handleCloseViewTransportModal();
                    }}
                  >
                    View reason
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="XCircle"
                    iconPosition="left"
                    onClick={() => {
                      openDisapproveModal("transport", viewTransportModal.row);
                      handleCloseViewTransportModal();
                    }}
                  >
                    Disapprove
                  </Button>
                )}

                {viewTransportModal.row?.approval_status !== "approved" && (
                  <Button
                    variant="success"
                    size="sm"
                    iconName="UserCheck"
                    iconPosition="left"
                    onClick={async () => {
                      const ok = await handleApproveTransport(
                        viewTransportModal.row,
                      );
                      if (ok) handleCloseViewTransportModal();
                    }}
                  >
                    Approve
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Civil Details Modal */}
      {viewCivilModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Civil Details
                {viewCivilModal.row?.customers?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewCivilModal.row.customers.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewCivilModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
              {Object.entries(viewCivilModal.row?.photos || {}).filter(
                ([, url]) => Boolean(url),
              ).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(viewCivilModal.row?.photos || {})
                    .filter(([, url]) => Boolean(url))
                    .map(([key, url]) => (
                      <div
                        key={key}
                        className="p-4 rounded-lg border border-border bg-muted/30"
                      >
                        <h3 className="text-sm font-semibold text-foreground mb-3">
                          {key.replace(/_/g, " ")}
                        </h3>
                        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                          <img
                            src={url}
                            alt={key.replace(/_/g, " ")}
                            className="w-full h-full object-contain bg-muted"
                          />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-border bg-muted/30 p-8 text-center text-muted-foreground">
                  No images uploaded
                </div>
              )}
            </div>

            <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
              <div className="text-xs text-muted-foreground">
                <p>
                  Submitted at:{" "}
                  <span className="font-medium text-foreground">
                    {getSubmissionMeta(viewCivilModal.row, "civil").submittedAt
                      ? new Date(
                          getSubmissionMeta(viewCivilModal.row, "civil")
                            .submittedAt,
                        ).toLocaleString("en-IN")
                      : "—"}
                  </span>
                </p>
                <p>
                  Submitted by:{" "}
                  <span className="font-medium text-foreground">
                    {
                      getSubmissionMeta(viewCivilModal.row, "civil")
                        .submitterLabel
                    }
                  </span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 ml-auto">
                {viewCivilModal.row?.approval_status === "disapproved" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="MessageSquareText"
                    iconPosition="left"
                    onClick={() => {
                      openViewReasonModalFor("civil", viewCivilModal.row);
                      handleCloseViewCivilModal();
                    }}
                  >
                    View reason
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="XCircle"
                    iconPosition="left"
                    onClick={() => {
                      openDisapproveModal("civil", viewCivilModal.row);
                      handleCloseViewCivilModal();
                    }}
                  >
                    Disapprove
                  </Button>
                )}

                {viewCivilModal.row?.approval_status !== "approved" && (
                  <Button
                    variant="success"
                    size="sm"
                    iconName="UserCheck"
                    iconPosition="left"
                    onClick={async () => {
                      const ok = await handleApproveCivil(viewCivilModal.row);
                      if (ok) handleCloseViewCivilModal();
                    }}
                  >
                    Approve
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Technical Details Modal */}
      {viewTechnicalModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Technical Details
                {viewTechnicalModal.row?.customers?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewTechnicalModal.row.customers.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewTechnicalModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
              {viewTechnicalModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                </div>
              ) : viewTechnicalModal.row ? (
                <TechnicalView
                  customer={viewTechnicalModal.row.customers}
                  technicalWork={viewTechnicalModal.row}
                />
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No technical data available.
                </div>
              )}
            </div>

            <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
              {!viewTechnicalModal.loading && viewTechnicalModal.row && (
                <>
                  <div className="text-xs text-muted-foreground mr-auto">
                    <p>
                      Submitted at:{" "}
                      <span className="font-medium text-foreground">
                        {getSubmissionMeta(viewTechnicalModal.row, "technical")
                          .submittedAt
                          ? new Date(
                              getSubmissionMeta(
                                viewTechnicalModal.row,
                                "technical",
                              ).submittedAt,
                            ).toLocaleString("en-IN")
                          : "—"}
                      </span>
                    </p>
                    <p>
                      Submitted by:{" "}
                      <span className="font-medium text-foreground">
                        {
                          getSubmissionMeta(viewTechnicalModal.row, "technical")
                            .submitterLabel
                        }
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 ml-auto">
                    {viewTechnicalModal.row?.approval_status ===
                    "disapproved" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquareText"
                        iconPosition="left"
                        onClick={() => {
                          openViewReasonModalFor(
                            "technical",
                            viewTechnicalModal.row,
                          );
                          handleCloseViewTechnicalModal();
                        }}
                      >
                        View reason
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="XCircle"
                        iconPosition="left"
                        onClick={() => {
                          openDisapproveModal(
                            "technical",
                            viewTechnicalModal.row,
                          );
                          handleCloseViewTechnicalModal();
                        }}
                      >
                        Disapprove
                      </Button>
                    )}

                    {viewTechnicalModal.row?.approval_status !== "approved" && (
                      <Button
                        variant="success"
                        size="sm"
                        iconName="UserCheck"
                        iconPosition="left"
                        onClick={handleApproveTechnicalFromModal}
                      >
                        Approve
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Commissioning Details Modal */}
      {viewCommissioningModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
              <h2 className="text-xl font-semibold text-foreground">
                Commissioning Details
                {viewCommissioningModal.row?.customers?.name && (
                  <span className="text-muted-foreground font-normal ml-2">
                    — {viewCommissioningModal.row.customers.name}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={handleCloseViewCommissioningModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0 space-y-6">
              {viewCommissioningModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                </div>
              ) : viewCommissioningModal.row ? (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      Testing
                    </h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div>
                        <dt className="text-muted-foreground">Submitted at</dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.commissioning_completed_at
                            ? new Date(
                                viewCommissioningModal.row
                                  .commissioning_completed_at,
                              ).toLocaleString("en-IN")
                            : "—"}
                        </dd>
                      </div>
                    </dl>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-sm mt-3">
                      <div>
                        <dt className="text-muted-foreground">
                          Panel number 1
                        </dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.panel_number_1 || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">
                          Panel number 2
                        </dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.panel_number_2 || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">
                          Panel number 3
                        </dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.panel_number_3 || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">
                          Panel number 4
                        </dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.panel_number_4 || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">
                          Inverter number
                        </dt>
                        <dd className="font-medium">
                          {viewCommissioningModal.row.inverter_number || "—"}
                        </dd>
                      </div>
                    </dl>
                    {viewCommissioningModal.row.voltage_meter_photo ? (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Voltage meter photo
                        </p>
                        <img
                          src={viewCommissioningModal.row.voltage_meter_photo}
                          alt="Voltage meter"
                          className="max-h-56 rounded-md border border-border object-contain"
                        />
                      </div>
                    ) : null}
                    {viewCommissioningModal.row.main_meter_serial_photo ? (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Main meter serial photo
                        </p>
                        <img
                          src={
                            viewCommissioningModal.row.main_meter_serial_photo
                          }
                          alt="Main meter serial"
                          className="max-h-56 rounded-md border border-border object-contain"
                        />
                      </div>
                    ) : null}
                    {viewCommissioningModal.row
                      .customer_photo_with_panels_side ? (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Customer photo with panels (side)
                        </p>
                        <img
                          src={
                            viewCommissioningModal.row
                              .customer_photo_with_panels_side
                          }
                          alt="Customer with panels side"
                          className="max-h-56 rounded-md border border-border object-contain"
                        />
                      </div>
                    ) : null}
                    {viewCommissioningModal.row
                      .customer_photo_with_panels_front ? (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Customer photo with panels (front)
                        </p>
                        <img
                          src={
                            viewCommissioningModal.row
                              .customer_photo_with_panels_front
                          }
                          alt="Customer with panels front"
                          className="max-h-56 rounded-md border border-border object-contain"
                        />
                      </div>
                    ) : null}
                    {viewCommissioningModal.row.customer_photo_with_inverter ? (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">
                          Customer photo with inverter
                        </p>
                        <img
                          src={
                            viewCommissioningModal.row
                              .customer_photo_with_inverter
                          }
                          alt="Customer with inverter"
                          className="max-h-56 rounded-md border border-border object-contain"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      Verification
                    </h3>
                    <ul className="text-sm space-y-1">
                      {[
                        ["cable_routing", "Cable routing"],
                        [
                          "lightning_arrester_checked",
                          "Lightning arrester checked",
                        ],
                        ["civil_structure", "Civil structure"],
                        ["earthing_pits", "Earthing pits"],
                        [
                          "inverter_functionality_confirmed",
                          "Inverter functionality confirmed",
                        ],
                        [
                          "safety_systems_operation",
                          "Safety systems operation",
                        ],
                      ].map(([key, label]) => (
                        <li key={key}>
                          {label}:{" "}
                          <span className="font-medium">
                            {viewCommissioningModal.row.verification?.[key]
                              ? "Yes"
                              : "No"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No commissioning data available.
                </div>
              )}
            </div>

            <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
              {!viewCommissioningModal.loading &&
                viewCommissioningModal.row && (
                  <>
                    <div className="text-xs text-muted-foreground mr-auto">
                      <p>
                        Submitted at:{" "}
                        <span className="font-medium text-foreground">
                          {getSubmissionMeta(
                            viewCommissioningModal.row,
                            "commissioning",
                          ).submittedAt
                            ? new Date(
                                getSubmissionMeta(
                                  viewCommissioningModal.row,
                                  "commissioning",
                                ).submittedAt,
                              ).toLocaleString("en-IN")
                            : "—"}
                        </span>
                      </p>
                      <p>
                        Submitted by:{" "}
                        <span className="font-medium text-foreground">
                          {
                            getSubmissionMeta(
                              viewCommissioningModal.row,
                              "commissioning",
                            ).submitterLabel
                          }
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 ml-auto">
                      {viewCommissioningModal.row?.approval_status ===
                      "disapproved" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="MessageSquareText"
                          iconPosition="left"
                          onClick={() => {
                            openViewReasonModalFor(
                              "commissioning",
                              viewCommissioningModal.row,
                            );
                            handleCloseViewCommissioningModal();
                          }}
                        >
                          View reason
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="XCircle"
                          iconPosition="left"
                          onClick={() => {
                            openDisapproveModal(
                              "commissioning",
                              viewCommissioningModal.row,
                            );
                            handleCloseViewCommissioningModal();
                          }}
                        >
                          Disapprove
                        </Button>
                      )}

                      {viewCommissioningModal.row?.approval_status !==
                        "approved" && (
                        <Button
                          variant="success"
                          size="sm"
                          iconName="UserCheck"
                          iconPosition="left"
                          onClick={handleApproveCommissioningFromModal}
                        >
                          Approve
                        </Button>
                      )}
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Disapprove Modal */}
      {disapproveModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Icon name="XCircle" size={20} className="text-destructive" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Disapprove
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Add an optional reason and mark this item as{" "}
                    <span className="font-mono">disapproved</span>.
                  </p>
                </div>
              </div>
              <button
                onClick={closeDisapproveModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={disapproveModal.saving}
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <Input
                label="Reason (optional)"
                type="text"
                placeholder="Why is this item disapproved?"
                value={disapproveModal.reason}
                onChange={(e) =>
                  setDisapproveModal((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeDisapproveModal}
                  disabled={disapproveModal.saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  iconName="XCircle"
                  iconPosition="left"
                  onClick={handleDisapprove}
                  loading={disapproveModal.saving}
                  disabled={disapproveModal.saving}
                >
                  Disapprove
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Disapproval Reason Modal */}
      {viewReasonModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Icon
                    name="MessageSquareText"
                    size={20}
                    className="text-muted-foreground"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Disapproval Reason
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {viewReasonModal.customerName}
                  </p>
                </div>
              </div>
              <button
                onClick={closeViewReasonModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-muted/30 border border-border rounded-lg">
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {viewReasonModal.reason || "—"}
                </p>
              </div>

              <div className="flex items-center justify-end pt-2">
                <Button variant="default" onClick={closeViewReasonModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Approvals;
